import { Fragment, useCallback, useEffect, useMemo, useState, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { getEcho } from '../lib/echo';
import { useAuth } from '../auth/AuthContext';
import type { AppNotification, AppNotificationData, Paginated } from '../api/types';

interface NotificationGroup {
  key: string;
  label: string;
  unreadCount: number;
  latestCreatedAt: string;
  items: AppNotification[];
}

const POLL_INTERVAL_MS = 30_000;

interface PaginatedWithMeta<T> extends Paginated<T> {
  meta: Paginated<T>['meta'] & { unread_count?: number };
}

export function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await apiFetch<{ unread_count: number }>(
        '/api/v1/notifications/unread-count'
      );
      setUnreadCount(res.unread_count);
    } catch {
      /* silent — bell should never crash the page */
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch<PaginatedWithMeta<AppNotification>>(
        '/api/v1/notifications?per_page=30'
      );
      setNotifications(res.data);
      if (typeof res.meta.unread_count === 'number') {
        setUnreadCount(res.meta.unread_count);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const groups: NotificationGroup[] = useMemo(() => {
    const byKey = new Map<string, NotificationGroup>();
    const orphanKey = '__other__';
    for (const n of notifications) {
      const carId = n.data.car_id;
      const carName = n.data.car_nickname;
      const key = carId ? `car:${carId}` : orphanKey;
      const label = carId && carName ? String(carName) : t('notifications.otherGroup');
      const existing = byKey.get(key);
      if (existing) {
        existing.items.push(n);
        if (!n.read_at) existing.unreadCount += 1;
        if (n.created_at > existing.latestCreatedAt) {
          existing.latestCreatedAt = n.created_at;
        }
      } else {
        byKey.set(key, {
          key,
          label,
          unreadCount: n.read_at ? 0 : 1,
          latestCreatedAt: n.created_at,
          items: [n],
        });
      }
    }
    const list: NotificationGroup[] = [];
    byKey.forEach((g) => {
      g.items.sort((a: AppNotification, b: AppNotification) =>
        b.created_at.localeCompare(a.created_at)
      );
      list.push(g);
    });
    // "Other" group always last; rest sorted by most recent
    list.sort((a: NotificationGroup, b: NotificationGroup) => {
      if (a.key === orphanKey) return 1;
      if (b.key === orphanKey) return -1;
      return b.latestCreatedAt.localeCompare(a.latestCreatedAt);
    });
    return list;
  }, [notifications, t]);

  useEffect(() => {
    fetchUnreadCount();
    const id = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchUnreadCount]);

  // Real-time: subscribe to the current user's private notification channel via Reverb.
  // Falls back gracefully if Reverb isn't configured — we still have the 30s poll above.
  useEffect(() => {
    if (!user) return;
    const echo = getEcho();
    if (!echo) return;

    const channel = echo.private(`App.Models.User.${user.id}`);
    const listener = (payload: AppNotificationData) => {
      setUnreadCount((c) => c + 1);
      if (payload.type === 'message.received') {
        enqueueSnackbar(
          t('notifications.newMessage', {
            sender: payload.sender_name ?? '—',
            car: payload.car_nickname ?? '—',
          }),
          {
            variant: 'info',
            action: (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  if (payload.conversation_id) {
                    navigate(`/inbox/${payload.conversation_id}`);
                  }
                }}
              >
                {t('common.open')}
              </Button>
            ),
          }
        );
      }
    };

    channel.notification(listener);

    return () => {
      echo.leave(`App.Models.User.${user.id}`);
    };
  }, [user, enqueueSnackbar, navigate, t]);

  const handleOpen = (e: MouseEvent<HTMLElement>) => {
    setAnchor(e.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => setAnchor(null);

  const handleMarkAllRead = async () => {
    await apiFetch('/api/v1/notifications/read-all', { method: 'POST' });
    setUnreadCount(0);
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, read_at: n.read_at ?? new Date().toISOString() }))
    );
  };

  const handleItemClick = async (n: AppNotification) => {
    handleClose();
    if (!n.read_at) {
      try {
        await apiFetch(`/api/v1/notifications/${n.id}/read`, { method: 'POST' });
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch {
        /* ignore */
      }
    }
    // Route by notification type.
    if (n.data.conversation_id) {
      navigate(`/inbox/${n.data.conversation_id}`);
      return;
    }
    if (n.data.type === 'contribution.requested' && n.data.car_id) {
      // Owner flow — jump straight to the Contributors tab on the car dashboard.
      // We don't have the slug here, so we rely on the car detail redirecting owners to garage.
      navigate(`/cars/${n.data.car_id}`);
      return;
    }
    if (
      (n.data.type === 'contribution.approved' || n.data.type === 'contribution.denied') &&
      n.data.car_id
    ) {
      navigate(`/cars/${n.data.car_id}`);
      return;
    }
    // Follow requests are answered, and followed garages read, on the garage page.
    if (n.data.type.startsWith('follow.')) {
      navigate('/garage');
    }
  };

  const renderNotificationText = (n: AppNotification): string => {
    // When the car is shown in the group header we use the short form
    // that omits "about {{car}}" to avoid redundancy.
    const hasCarHeader = Boolean(n.data.car_id);
    const car = n.data.car_nickname ?? '—';
    if (n.data.type === 'message.received') {
      return hasCarHeader
        ? t('notifications.newMessageFrom', { sender: n.data.sender_name ?? '—' })
        : t('notifications.newMessage', { sender: n.data.sender_name ?? '—', car });
    }
    if (n.data.type === 'car.rated') {
      const rater = (n.data as { rater_name?: string }).rater_name ?? '—';
      const score = (n.data as { score?: number }).score ?? '?';
      return hasCarHeader
        ? t('notifications.newRatingFrom', { rater, score })
        : t('notifications.newRating', { rater, car, score });
    }
    if (n.data.type === 'contribution.requested') {
      const requester = (n.data as { requester_name?: string }).requester_name ?? '—';
      return hasCarHeader
        ? t('notifications.contributionRequestedFrom', { requester })
        : t('notifications.contributionRequested', { requester, car });
    }
    if (n.data.type === 'contribution.approved') {
      return hasCarHeader
        ? t('notifications.contributionApprovedFrom')
        : t('notifications.contributionApproved', { car });
    }
    if (n.data.type === 'contribution.denied') {
      return hasCarHeader
        ? t('notifications.contributionDeniedFrom')
        : t('notifications.contributionDenied', { car });
    }
    const garage = (n.data as { garage_name?: string }).garage_name ?? '—';
    if (n.data.type === 'follow.requested') {
      const requester = (n.data as { requester_name?: string }).requester_name ?? '—';
      return t('notifications.followRequested', { requester, garage });
    }
    if (n.data.type === 'follow.approved') {
      return t('notifications.followApproved', { garage });
    }
    if (n.data.type === 'follow.denied') {
      return t('notifications.followDenied', { garage });
    }
    return n.data.type;
  };

  return (
    <>
      <IconButton
        color="inherit"
        aria-label={t('nav.notifications')}
        onClick={handleOpen}
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={handleClose}
        slotProps={{ paper: { sx: { width: 360, maxHeight: 480 } } }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            {t('notifications.title')}
          </Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={handleMarkAllRead}>
              {t('notifications.markAllRead')}
            </Button>
          )}
        </Box>
        <Divider />
        {loading && notifications.length === 0 && (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {t('common.loading')}
            </Typography>
          </Box>
        )}
        {!loading && notifications.length === 0 && (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {t('notifications.empty')}
            </Typography>
          </Box>
        )}
        {groups.map((group) => (
          <Fragment key={group.key}>
            <Box
              sx={{
                px: 2,
                py: 0.75,
                bgcolor: 'background.default',
                position: 'sticky',
                top: 0,
                zIndex: 1,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
              >
                <Typography variant="caption" fontWeight={700} sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  {group.label}
                </Typography>
                {group.unreadCount > 0 && (
                  <Chip
                    size="small"
                    color="error"
                    label={group.unreadCount}
                    sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                  />
                )}
              </Stack>
            </Box>
            {group.items.map((n) => (
              <MenuItem
                key={n.id}
                onClick={() => handleItemClick(n)}
                sx={{
                  whiteSpace: 'normal',
                  alignItems: 'flex-start',
                  pl: 3,
                  bgcolor: n.read_at ? undefined : 'action.hover',
                }}
              >
                <ListItemText
                  primary={renderNotificationText(n)}
                  secondary={
                    n.data.preview
                      ? String(n.data.preview)
                      : new Date(n.created_at).toLocaleString()
                  }
                  slotProps={{
                    primary: { variant: 'body2', fontWeight: n.read_at ? 400 : 600 },
                    secondary: { variant: 'caption' },
                  }}
                />
              </MenuItem>
            ))}
          </Fragment>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            handleClose();
            navigate('/inbox');
          }}
        >
          <ListItemText
            primary={t('notifications.viewAll')}
            slotProps={{ primary: { variant: 'body2', textAlign: 'center' } }}
          />
        </MenuItem>
      </Menu>
    </>
  );
}
